/** The Export button + format menu, shared by every artifact card.
 *
 *  Extracted from ArtifactTableCard, which had the only copy. The markup is
 *  forty lines with a non-obvious branch in the middle (link vs fetch), and it
 *  was about to be pasted into three more cards — `lib/download.ts` already
 *  records what the copy-pasted version of an export path costs.
 *
 *  The branch is the reason this is a component rather than a snippet: outside
 *  the embed a plain link is enough (same origin, session cookie, the browser
 *  applies Content-Disposition, and a refusal renders the route's own message
 *  in the opened tab). The embed is cross-origin with no cookie, so a relative
 *  href would resolve against the HOST page and arrive unauthenticated — there
 *  the bytes are fetched with the bearer and handed to the downloader.
 */
import React, { useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import { Download } from "lucide-react";

import {
  useArtifactExport,
  type ArtifactExportFormat,
} from "./useArtifactExport";
import { usePortalScope } from "@/analytics/PortalScope";

export interface ArtifactExportOption {
  fmt: ArtifactExportFormat;
  label: string;
}

export function ArtifactExportMenu({
  artifactId,
  title,
  formats,
  disabled = false,
}: {
  artifactId: string;
  title: string | null;
  formats: readonly ArtifactExportOption[];
  /** Hide the affordance entirely — a body the route would refuse. Rendering a
   *  button that always fails is worse than rendering none. */
  disabled?: boolean;
}) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  // Portaled: the card's `artifact` scope does not reach the menu.
  const menuScope = usePortalScope("artifact");
  const { isEmbed, busy, error, hrefFor, runEmbedExport } = useArtifactExport(
    artifactId,
    title,
  );

  if (disabled || formats.length === 0) return null;

  return (
    <Box
      sx={{
        display: "inline-flex",
        flexDirection: "column",
        alignItems: "flex-end",
      }}
    >
      <Button
        size="small"
        variant="outlined"
        data-ph="open_export"
        onClick={(e) => setAnchor(e.currentTarget)}
        aria-haspopup="menu"
        aria-expanded={Boolean(anchor)}
        // A spinner while the server renders, so a slow export does not read
        // as a dead button.
        startIcon={
          busy ? <CircularProgress size={14} /> : <Download size={14} />
        }
        sx={{ textTransform: "none", fontSize: "0.75rem", py: 0.25, px: 1 }}
      >
        Export
      </Button>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        slotProps={{ list: menuScope }}
      >
        {/* The tag is spelled out per format so it is never built from data. */}
        {formats.map(({ fmt, label }) =>
          isEmbed ? (
            <MenuItem
              key={fmt}
              data-ph={
                fmt === "html" ? "export_html"
                : fmt === "pdf" ? "export_pdf"
                : fmt === "docx" ? "export_docx"
                : fmt === "pptx" ? "export_pptx"
                : "export_xlsx"
              }
              disabled={busy}
              onClick={() => {
                setAnchor(null);
                void runEmbedExport(fmt);
              }}
            >
              {label}
            </MenuItem>
          ) : (
            <MenuItem
              key={fmt}
              data-ph={
                fmt === "html" ? "export_html"
                : fmt === "pdf" ? "export_pdf"
                : fmt === "docx" ? "export_docx"
                : fmt === "pptx" ? "export_pptx"
                : "export_xlsx"
              }
              component="a"
              href={hrefFor(fmt)}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setAnchor(null)}
            >
              {label}
            </MenuItem>
          ),
        )}
      </Menu>
      {error && (
        // In the card, not a toast: the failure belongs next to the artifact it
        // was about, and the embed mounts no notifications provider. This used
        // to live in ArtifactTableCard, so only that one card reported a failed
        // export — every other card failed silently.
        <Typography
          role="alert"
          variant="caption"
          sx={{ display: "block", mt: 0.5, color: "error.main" }}
        >
          {error}
        </Typography>
      )}
    </Box>
  );
}
